import React, { useEffect, useMemo, useRef, useState } from 'react';
import { gql, useApolloClient, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { showInkToast } from '../cms/InkToast';
import { currentUrl, MediaUrlPicker, type LibraryAsset } from '../content/MediaUrlField';
import { uploadAsset } from '../../lib/media/uploadAsset';
import { WritePost, type PostCampaign } from './WritePost';
import { PostPanel } from './PostPanel';
import { usePostActions } from './postActions';
import {
  calendarDate,
  Channel,
  channelAccount,
  channelMark,
  channelName,
  channelWho,
  isPersonalProfile,
} from './reach';
import { UPDATE_PROJECT } from '../../graphql/mutations';
import {
  aOrAn,
  Board,
  BoardCard,
  BoardContainer,
  byDue,
  CampaignLink,
  cantPost,
  cleanLinkUrl,
  CREATE_CAMPAIGN_ITEM,
  daysFromToday,
  dueDay,
  dueFull,
  dueWords,
  firstName,
  INVITE_TO_CAMPAIGN,
  isNotTracked,
  linkName,
  MAX_CAMPAIGN_LINKS,
  needWords,
  notTrackedLine,
  SAVE_CAMPAIGN_ARTICLE,
  SAVE_CAMPAIGN_LANDING,
  SAVE_CAMPAIGN_LINKS,
  SAVE_CAMPAIGN_TASK,
  SAVE_CAMPAIGN_WORK,
  statusWords,
  toYmd,
  ADD_CAMPAIGN_CHANNEL,
  type AddableChannel,
} from './campaignCards';

/**
 * The campaign board (TinyCMS UX optimization review, 2026-10-23): the
 * campaign's work in containers — the website, each connected channel, other
 * work — of cards. A card shows its due date (when it should be ready, not
 * when it goes out), what's still missing, its status, and a note chip:
 * hover the chip to read the notes, click it (or "+ Note" on a hovered card)
 * to edit them and the due date. Clicking a card opens it: a post in Write a
 * post, everything else in the drawer on the right.
 *
 * A Campaign-role person works here like anyone on the team, but never
 * approves: their posts are sent for approval and statuses say who approves
 * ("Waiting on Hai").
 */

interface CampaignLite {
  id: string;
  name: string;
  startDate?: string | null;
  createdAt?: string | null;
  deadline?: string | null;
  status?: string;
}

const CONTAINER_BADGE: Record<string, string> = { website: '▤', other: '✓' };
const NO_LOST_CHANNELS: ReadonlySet<string> = new Set();

/**
 * Ready: done, scheduled to go out, or every detail in. A ready card met its
 * due date (when it should be ready), so it is never overdue (backlog b35b62cf).
 */
// A Not tracked landing page is never ready: it's left out of the counts.
const isReady = (c: BoardCard) =>
  !isNotTracked(c) && (c.done || Boolean(c.dueMet) || c.fields.every((f) => f.ok));

// ── Team ─────────────────────────────────────────────────────────────────────

const initialsOf = (name: string, email: string, pending: boolean) =>
  pending
    ? email.slice(0, 2).toUpperCase()
    : name
        .split(/\s+/)
        .map((x) => x[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

export function CampaignTeamRow({
  campaign,
  board,
  onChanged,
}: {
  campaign: CampaignLite;
  board: Board | null;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [invite, { loading }] = useMutation(INVITE_TO_CAMPAIGN);
  if (!board) return null;
  const trimmed = email.trim().toLowerCase();
  const ok =
    /^\S+@\S+\.\S+$/.test(trimmed) && !board.team.some((t) => t.email.toLowerCase() === trimmed);
  const send = async () => {
    if (!ok || loading) return;
    try {
      await invite({ variables: { campaignId: campaign.id, email: trimmed } });
      showInkToast(`Invite sent to ${trimmed}. They’ll see only ${campaign.name}.`);
      setOpen(false);
      setEmail('');
      onChanged();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    }
  };
  return (
    <div className="cb-team" aria-label="Campaign team">
      <span className="cb-team-label">Team</span>
      <div className="cb-team-people">
        {board.team.map((t) => (
          <span
            key={t.userId}
            className="cb-person"
            title={t.email}
            data-pending={t.pending ? 'true' : undefined}
          >
            <span
              className="cb-person-av"
              data-tone={t.you ? 'me' : t.role === 'Campaign' ? 'campaign' : undefined}
            >
              {initialsOf(t.name, t.email, t.pending)}
            </span>
            {t.pending ? t.email : t.name}
            {t.you ? ' (you)' : ''}
            <span className="cb-person-role">{t.pending ? 'Invited' : t.role}</span>
          </span>
        ))}
      </div>
      {board.viewer.canInvite ? (
        <button type="button" className="cb-team-invite" onClick={() => setOpen((v) => !v)}>
          + Invite
        </button>
      ) : null}
      {open ? (
        <>
          <div className="cb-scrim" onClick={() => setOpen(false)} />
          <div className="cb-invite" role="dialog" aria-label={`Invite to ${campaign.name}`}>
            <div className="cb-invite-title">Invite to {campaign.name}</div>
            <label className="cb-field-label" htmlFor="cb-invite-email">
              Email
            </label>
            <input
              id="cb-invite-email"
              className="cb-input"
              type="email"
              autoFocus
              value={email}
              placeholder="Email"
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void send();
              }}
            />
            <div className="cb-invite-note">
              <span className="cb-chip">Campaign</span>
              <span>
                Sees only the campaigns they’re on, and can edit everything in them: the landing
                page, articles and posts. Nothing goes live until you approve it.
              </span>
            </div>
            <div className="cb-actions">
              <button type="button" className="ink-btn" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                disabled={!ok || loading}
                onClick={() => void send()}
              >
                {loading ? 'Sending…' : 'Send invite'}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

// ── Links ────────────────────────────────────────────────────────────────────

/**
 * The campaign's named links (backlog 90258646): the material behind it that
 * lives outside tinycms — a Drive folder, a Notion page, drafts. Anyone on the
 * campaign edits them; agents read and write them too (save_campaign links).
 */
export function CampaignLinksRow({
  campaign,
  board,
  onChanged,
}: {
  campaign: CampaignLite;
  board: Board | null;
  onChanged: () => void;
}) {
  const [rows, setRows] = useState<CampaignLink[] | null>(null);
  const [save, { loading }] = useMutation(SAVE_CAMPAIGN_LINKS);
  // A server that predates links sends none: no row to add them to.
  if (!board || !board.links) return null;
  const links = board.links;
  const open = () =>
    setRows(links.length ? links.map((l) => ({ ...l })) : [{ label: '', url: '' }]);
  const filled = (rows || []).filter((r) => r.label.trim() || r.url.trim());
  const problem = filled.find((r) => !r.label.trim())
    ? 'Give each link a name, like “Project folder”.'
    : filled.find((r) => !cleanLinkUrl(r.url))
      ? 'Each link needs a web address, like https://drive.google.com/…'
      : null;
  const set = (i: number, patch: Partial<CampaignLink>) =>
    setRows((r) => (r ? r.map((x, j) => (j === i ? { ...x, ...patch } : x)) : r));
  const submit = async () => {
    if (problem || loading) return;
    try {
      await save({
        variables: {
          campaignId: campaign.id,
          links: filled.map((r) => ({ label: r.label.trim(), url: cleanLinkUrl(r.url) })),
        },
      });
      showInkToast(filled.length ? 'Links saved' : 'Links removed');
      setRows(null);
      onChanged();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    }
  };
  return (
    <div className="cb-team cb-links" aria-label="Campaign links">
      <span className="cb-team-label">Links</span>
      {links.length ? (
        <div className="cb-team-people">
          {links.map((l, i) => (
            <a
              key={`${i}:${l.url}`}
              className="cb-person cb-link"
              href={l.url}
              target="_blank"
              rel="noopener noreferrer"
              title={l.url}
            >
              <span aria-hidden="true">↗</span>
              {linkName(l)}
            </a>
          ))}
        </div>
      ) : null}
      <button type="button" className="cb-team-invite" onClick={open}>
        {links.length ? 'Edit' : '+ Add a link'}
      </button>
      {rows ? (
        <>
          <div className="cb-scrim" onClick={() => setRows(null)} />
          <div
            className="cb-invite cb-links-edit"
            role="dialog"
            aria-label={`Links for ${campaign.name}`}
          >
            <div className="cb-invite-title">Links for {campaign.name}</div>
            {rows.map((r, i) => (
              <div className="cb-link-row" key={i}>
                <input
                  className="cb-input"
                  aria-label="Name"
                  placeholder="Name"
                  value={r.label}
                  autoFocus={i === rows.length - 1 && !r.label}
                  onChange={(e) => set(i, { label: e.target.value })}
                />
                <input
                  className="cb-input"
                  aria-label="Web address"
                  placeholder="https://example.com/folder"
                  value={r.url}
                  onChange={(e) => set(i, { url: e.target.value })}
                />
                <button
                  type="button"
                  className="cb-x"
                  aria-label="Remove this link"
                  onClick={() => setRows((x) => (x ? x.filter((_, j) => j !== i) : x))}
                >
                  ×
                </button>
              </div>
            ))}
            {rows.length < MAX_CAMPAIGN_LINKS ? (
              <button
                type="button"
                className="cb-team-invite"
                style={{ alignSelf: 'flex-start' }}
                onClick={() => setRows((x) => [...(x || []), { label: '', url: '' }])}
              >
                + Add another
              </button>
            ) : null}
            <div className="cb-invite-note">
              <span>
                Files and docs this campaign needs, like a Drive folder or a Notion page. Everyone
                on the campaign sees them, and your agent can read and add them too.
              </span>
            </div>
            {problem ? <div className="cb-link-problem">{problem}</div> : null}
            <div className="cb-actions">
              <button type="button" className="ink-btn" onClick={() => setRows(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                disabled={Boolean(problem) || loading}
                onClick={() => void submit()}
              >
                {loading ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

// ── Board ────────────────────────────────────────────────────────────────────

interface NoteEdit {
  key: string;
  subject: BoardCard['subject'];
  id: string;
  text: string;
  due: string;
  /** The card's own file or doc (backlog 90258646). */
  linkUrl: string;
  linkLabel: string;
}

export function CampaignBoardView({
  accountId,
  campaign,
  board,
  ended,
  onChanged,
  openCardKey = null,
  onCardOpened,
  lostChannels = NO_LOST_CHANNELS,
  onReconnect,
}: {
  accountId: string;
  campaign: CampaignLite;
  board: Board;
  ended: boolean;
  onChanged: () => Promise<unknown> | void;
  /** Channels whose connection expired, by id (connectionExpired); none once ended. */
  lostChannels?: ReadonlySet<string>;
  /** Absent for someone who can't reconnect: the column only says it expired. */
  onReconnect?: (socialProfileId: string) => void;
  /** Open this card once the board is in (Campaigns → Calendar's marks). */
  openCardKey?: string | null;
  onCardOpened?: () => void;
}) {
  const navigate = useNavigate();
  const [hover, setHover] = useState<string | null>(null);
  const [peek, setPeek] = useState<string | null>(null);
  const [edit, setEdit] = useState<NoteEdit | null>(null);
  const [drawer, setDrawer] = useState<{ card: BoardCard; container: BoardContainer } | null>(null);
  // Write a post: "+ Add a … post" (the modal is only for a new post).
  const [writing, setWriting] = useState<{
    draft?: BoardCard['post'];
    channelId?: string;
  } | null>(null);
  // A post card opens the side panel, whatever its status (2026-10-24).
  const [panelPostId, setPanelPostId] = useState<string | null>(null);
  const panelPost =
    board.containers.flatMap((c) => c.cards).find((c) => c.post?.id === panelPostId)?.post || null;
  const {
    busy: postBusy,
    act,
    remove,
  } = usePostActions({
    onDone: () => {
      setPanelPostId(null);
      return onChanged();
    },
  });
  const [saveWork, { loading: savingNote }] = useMutation(SAVE_CAMPAIGN_WORK);
  const [createItem] = useMutation(CREATE_CAMPAIGN_ITEM);
  const [updateCampaign] = useMutation(UPDATE_PROJECT);
  // Website's "+ Add a page or article" menu, open (round 34).
  const [addMenu, setAddMenu] = useState(false);
  const approver = firstName(board.approver?.name);
  const canApprove = board.viewer.canApprove;

  // A card opened after it was added: find it once the board is back.
  const [openKey, setOpenKey] = useState<string | null>(openCardKey);
  useEffect(() => {
    if (openCardKey) setOpenKey(openCardKey);
  }, [openCardKey]);
  useEffect(() => {
    if (!openKey) return;
    for (const col of board.containers) {
      const card = col.cards.find((c) => c.key === openKey);
      if (card) {
        // A post opens in the side panel, anything else in the drawer.
        if (card.type === 'post' && card.post) {
          setPanelPostId(card.post.id);
        } else if (card.type !== 'post') {
          setDrawer({ card, container: col });
        }
        setOpenKey(null);
        onCardOpened?.();
        return;
      }
    }
  }, [openKey, board, onCardOpened]);

  const all = board.containers.flatMap((c) => c.cards);
  const lostCol = (c: BoardContainer) =>
    c.type === 'channel' && Boolean(c.socialProfileId && lostChannels.has(c.socialProfileId));
  // Ready otherwise, but it can't go out: never counted as ready (backlog 33999dec).
  const blocked = board.containers
    .filter(lostCol)
    .flatMap((c) => c.cards.filter((card) => cantPost(card) && isReady(card))).length;
  const open = all.filter((c) => !c.done);
  const due = (c: BoardCard) => {
    const d = dueDay(c);
    return d ? daysFromToday(d) : Infinity;
  };
  const todo = open.filter((c) => !isReady(c));
  const late = todo.filter((c) => due(c) < 0).length;
  const dueToday = todo.filter((c) => due(c) === 0).length;
  const dueTomorrow = todo.filter((c) => due(c) === 1).length;
  // "7 pieces · 1 ready · 2 due today, 1 tomorrow" (UX review round 20):
  // finished work counts as ready, and the days match the cards' own words.
  const summary = [
    `${all.length} ${all.length === 1 ? 'piece' : 'pieces'}`,
    `${all.length - todo.length - blocked} ready`,
    [
      late && `${late} overdue`,
      dueToday && `${dueToday} due today`,
      dueTomorrow && (dueToday ? `${dueTomorrow} tomorrow` : `${dueTomorrow} due tomorrow`),
    ]
      .filter(Boolean)
      .join(', '),
  ]
    .filter(Boolean)
    .join(' · ');

  const channels: Channel[] = useMemo(
    () =>
      board.containers
        .filter((c) => c.type === 'channel' && c.active)
        .map((c) => ({
          id: c.socialProfileId || c.key,
          platform: c.platform || '',
          profileId: c.profileId,
          profileName: c.name,
          profileUsername: c.username,
          isActive: true,
          approvalMode: 'ask',
        })),
    [board]
  );
  const campaignsForPost: PostCampaign[] = useMemo(
    () => [
      {
        id: campaign.id,
        name: campaign.name,
        kind: 'campaign',
        status: campaign.status || 'active',
        deadline: campaign.deadline,
        startDate: campaign.startDate,
        createdAt: campaign.createdAt,
        channels: channels.map((c) => ({ id: c.id })),
      },
    ],
    [campaign, channels]
  );

  const saveNote = async () => {
    if (!edit) return;
    const text = edit.text.trim();
    const url = cleanLinkUrl(edit.linkUrl);
    if (edit.linkUrl.trim() && !url) {
      showInkToast('The link needs a web address, like https://docs.google.com/…');
      return;
    }
    const card = board.containers.flatMap((c) => c.cards).find((c) => c.key === edit.key);
    const label = url ? edit.linkLabel.trim() : '';
    const linkChanged =
      (card?.link?.url || '') !== (url || '') || (card?.link?.label || '') !== label;
    try {
      await saveWork({
        variables: {
          subjectKind: edit.subject,
          subjectId: edit.id,
          note: text,
          ...(edit.due ? { dueDate: edit.due } : {}),
          ...(linkChanged ? { link: url ? { url, label } : null } : {}),
        },
      });
      showInkToast(text || url ? 'Saved' : 'Note removed');
      setEdit(null);
      await onChanged();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    }
  };

  const openCard = (card: BoardCard, container: BoardContainer) => {
    if (card.type === 'post' && card.post) {
      setPanelPostId(card.post.id);
      return;
    }
    setDrawer({ card, container });
  };

  /** Website → "+ Add an article"; Other work → "+ Add a task". */
  const addItem = async (kind: 'article' | 'task') => {
    const now = new Date();
    const start = campaign.startDate ? new Date(campaign.startDate) : null;
    const d =
      kind === 'article' && start && start > now
        ? new Date(start.getFullYear(), start.getMonth(), start.getDate() - 2)
        : new Date(now.getFullYear(), now.getMonth(), now.getDate() + 3);
    try {
      const { data } = await createItem({
        variables: { campaignId: campaign.id, kind, dueDate: toYmd(d) },
      });
      const id = data?.createCampaignItem as string | undefined;
      await onChanged();
      if (id) setOpenKey(`item:${id}`);
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    }
  };

  /**
   * Make a campaign page (round 34): the campaign's landing page becomes
   * "Agent makes one", so its agent drafts a page with a sign-up form, which
   * waits for approval. Sign-ups there count toward the campaign.
   */
  const makeCampaignPage = async () => {
    try {
      await updateCampaign({
        variables: {
          id: campaign.id,
          input: { landingPageStatus: 'agent', landingPageUrl: null },
        },
      });
      setDrawer(null);
      await onChanged();
      showInkToast(
        'Your agent makes a campaign page with a sign-up form. It waits for approval like any page.'
      );
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    }
  };
  const landingCard = board.containers.flatMap((c) => c.cards).find((c) => c.type === 'landing');
  // The menu offers a campaign landing page while the campaign has no page of its own.
  const noOwnPage = !landingCard || isNotTracked(landingCard);

  const containerName = (c: BoardContainer) =>
    c.type === 'website'
      ? 'Website'
      : c.type === 'other'
        ? 'Other work'
        : channelName(c.platform || '');
  const containerSub = (c: BoardContainer) =>
    c.type === 'website'
      ? board.siteHost || 'Your site'
      : c.type === 'other'
        ? 'Tasks that aren’t a post or a page'
        : [
            c.username ? `@${c.username.replace(/^@/, '')}` : c.name || '',
            isPersonalProfile(c.platform || '', c.profileId) ? 'personal profile' : '',
            c.active ? '' : 'disconnected',
          ]
            .filter(Boolean)
            .join(' · ');
  const addLabel = (c: BoardContainer) =>
    c.type === 'website'
      ? '+ Add a page or article'
      : c.type === 'other'
        ? '+ Add a task'
        : `+ Add ${aOrAn(channelName(c.platform || ''))} ${channelName(c.platform || '')} post`;
  const onAdd = (c: BoardContainer) => {
    if (c.type === 'website') setAddMenu((v) => !v);
    else if (c.type === 'other') void addItem('task');
    else setWriting({ channelId: c.socialProfileId });
  };
  const hasChannels = board.containers.some((c) => c.type === 'channel');
  // Empty for a Campaign member (the server doesn't send them) and once ended.
  const addable = ended ? [] : board.addableChannels || [];
  const [addingChannel, setAddingChannel] = useState(false);
  const [addChannel, { loading: addingBusy }] = useMutation(ADD_CAMPAIGN_CHANNEL);
  const onAddChannel = async (c: AddableChannel) => {
    const named = channelAccount({
      platform: c.platform,
      profileName: c.name,
      profileUsername: c.username,
    });
    try {
      await addChannel({ variables: { campaignId: campaign.id, socialProfileId: c.id } });
      setAddingChannel(false);
      await onChanged();
      showInkToast(`${named} added to ${campaign.name}.`);
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That channel couldn’t be added.');
    }
  };
  const addChannelTile = addable.length ? (
    <div className="cb-add-channel" data-screen-label="Add a channel">
      <button
        type="button"
        className="cb-add-channel-btn"
        aria-haspopup="menu"
        aria-expanded={addingChannel}
        onClick={() => setAddingChannel((v) => !v)}
      >
        + Add a channel to this campaign
      </button>
      {addingChannel ? (
        <div className="cb-add-channel-menu" role="menu">
          {addable.map((c) => (
            <button
              type="button"
              role="menuitem"
              key={c.id}
              disabled={addingBusy}
              onClick={() => void onAddChannel(c)}
            >
              <span className="cb-col-badge">{channelMark(c.platform)}</span>
              <span style={{ minWidth: 0 }}>
                <span className="cb-add-channel-name">{channelName(c.platform)}</span>
                <span className="cb-add-channel-acct">
                  {channelWho({ profileName: c.name, profileUsername: c.username })}
                  {isPersonalProfile(c.platform, c.profileId) ? ' · posts as you' : ''}
                </span>
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  ) : null;

  return (
    <div className="cb" aria-label="Campaign board">
      <div className="cb-head">
        <span className="cb-head-title">Work</span>
        <span className="cb-head-sum">{summary}</span>
        <span className="cb-head-note">
          Due dates are when each piece should be ready, not when it goes out.
        </span>
      </div>
      <div className="cb-grid">
        {board.containers.map((col) => (
          <React.Fragment key={col.key}>
            {col.type === 'other' ? addChannelTile : null}
            <div
              className="cb-col"
              aria-label={containerName(col)}
              data-off={col.type === 'channel' && !col.active ? 'true' : undefined}
            >
              <div className="cb-col-head">
                <span className="cb-col-badge">
                  {col.type === 'channel'
                    ? channelMark(col.platform || '')
                    : CONTAINER_BADGE[col.type]}
                </span>
                <div className="cb-col-names">
                  <div className="cb-col-name">{containerName(col)}</div>
                  <div className="cb-col-sub">{containerSub(col)}</div>
                </div>
                <span className="cb-col-count">{col.cards.length}</span>
              </div>
              {lostCol(col) ? (
                <div className="cb-col-lost" role="status">
                  <span className="rch-expired-dot" aria-hidden="true" />
                  <span>
                    Connection expired
                    {onReconnect ? (
                      <>
                        {' · '}
                        <button
                          type="button"
                          className="ink-quiet-link"
                          onClick={() => onReconnect(col.socialProfileId as string)}
                        >
                          Reconnect
                        </button>
                      </>
                    ) : null}
                  </span>
                </div>
              ) : null}
              <div className="cb-cards">
                {byDue(col.cards).map((card) => (
                  <CardView
                    key={card.key}
                    card={card}
                    cantGoOut={lostCol(col) && cantPost(card)}
                    campaignEnd={calendarDate(campaign.deadline)}
                    approver={approver}
                    canApprove={canApprove}
                    hovered={hover === card.key}
                    peeking={peek === card.key}
                    editing={edit?.key === card.key ? edit : null}
                    saving={savingNote}
                    onEnter={() => setHover(card.key)}
                    onLeave={() => {
                      setHover((h) => (h === card.key ? null : h));
                      setPeek((p) => (p === card.key ? null : p));
                    }}
                    onPeek={() => setPeek(card.key)}
                    onEdit={() => {
                      const d = dueDay(card);
                      setPeek(null);
                      setEdit({
                        key: card.key,
                        subject: card.subject,
                        id: card.id,
                        text: card.note,
                        due: d ? toYmd(d) : '',
                        linkUrl: card.link?.url || '',
                        linkLabel: card.link?.label || '',
                      });
                    }}
                    onEditChange={(patch) => setEdit((e) => (e ? { ...e, ...patch } : e))}
                    onEditCancel={() => setEdit(null)}
                    onEditSave={() => void saveNote()}
                    onOpen={() => openCard(card, col)}
                  />
                ))}
                {!col.cards.length ? (
                  <div className="cb-empty">
                    {col.type === 'other'
                      ? 'Add anything else this campaign needs, like printing, a partner email or a sign-off.'
                      : `Nothing planned for ${containerName(col)} yet.`}
                  </div>
                ) : null}
              </div>
              {ended || (col.type === 'channel' && !col.active) ? (
                <div className="cb-col-foot" />
              ) : (
                <>
                  <button
                    type="button"
                    className="cb-add"
                    aria-expanded={col.type === 'website' ? addMenu : undefined}
                    onClick={() => onAdd(col)}
                  >
                    {addLabel(col)}
                  </button>
                  {col.type === 'website' && addMenu ? (
                    <div className="cb-add-menu" role="menu" aria-label="Add a page or article">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setAddMenu(false);
                          void addItem('article');
                        }}
                      >
                        <span className="cb-add-menu-name">Article</span>
                        <span className="cb-add-menu-sub">Written for this campaign</span>
                      </button>
                      {noOwnPage && canApprove ? (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setAddMenu(false);
                            void makeCampaignPage();
                          }}
                        >
                          <span className="cb-add-menu-name">Campaign landing page</span>
                          <span className="cb-add-menu-sub">
                            {landingCard
                              ? 'Replaces the existing page. Sign-ups then count.'
                              : 'Your agent makes one, with a sign-up form.'}
                          </span>
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </React.Fragment>
        ))}
        {!hasChannels && !addable.length ? (
          <div className="cb-connect">
            <div className="cb-connect-title">Add a social channel</div>
            <p>
              Each channel you connect becomes a column here, so you can plan posts that point to
              this campaign.
            </p>
            {canApprove ? (
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                onClick={() => navigate('/reach/channels')}
              >
                Connect a channel
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {drawer && isNotTracked(drawer.card) ? (
        <NotTrackedDialog
          card={drawer.card}
          canMake={canApprove}
          onKeep={() => {
            setDrawer(null);
            const name = drawer.card.landing?.title || 'that page';
            showInkToast(`Kept. Posts still link to ${name}.`);
          }}
          onMake={makeCampaignPage}
        />
      ) : drawer ? (
        <CardDrawer
          accountId={accountId}
          campaign={campaign}
          card={drawer.card}
          board={board}
          approver={approver}
          onClose={() => setDrawer(null)}
          onSaved={async () => {
            setDrawer(null);
            await onChanged();
          }}
        />
      ) : null}
      {panelPost ? (
        <PostPanel
          post={panelPost}
          busy={postBusy === panelPost.id}
          onClose={() => setPanelPostId(null)}
          onAct={(kind, at, reason) => void act(panelPost, kind, at, reason)}
          onDelete={() => void remove(panelPost)}
          onSaved={() => {
            setPanelPostId(null);
            return onChanged();
          }}
          approver={canApprove ? null : approver || 'The owner'}
          channelList={canApprove ? null : channels}
          campaignList={canApprove ? null : campaignsForPost}
        />
      ) : null}
      {writing ? (
        <WritePost
          campaignId={campaign.id}
          draft={writing.draft || null}
          channelIds={writing.channelId ? [writing.channelId] : null}
          approver={canApprove ? null : approver || 'The owner'}
          channelList={canApprove ? null : channels}
          campaignList={canApprove ? null : campaignsForPost}
          onClose={() => setWriting(null)}
          onPosted={() => {
            setWriting(null);
            void onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

function CardView({
  card,
  cantGoOut,
  approver,
  canApprove,
  hovered,
  peeking,
  editing,
  saving,
  onEnter,
  onLeave,
  onPeek,
  onEdit,
  onEditChange,
  onEditCancel,
  onEditSave,
  onOpen,
  campaignEnd,
}: {
  card: BoardCard;
  /** Its channel's connection expired and it hasn't gone out. */
  cantGoOut: boolean;
  /** The campaign's last day, so work due after it says so. */
  campaignEnd?: Date | null;
  approver: string | null;
  canApprove: boolean;
  hovered: boolean;
  peeking: boolean;
  editing: NoteEdit | null;
  saving: boolean;
  onEnter: () => void;
  onLeave: () => void;
  onPeek: () => void;
  onEdit: () => void;
  onEditChange: (patch: Partial<NoteEdit>) => void;
  onEditCancel: () => void;
  onEditSave: () => void;
  onOpen: () => void;
}) {
  const d = dueDay(card);
  const due = dueWords(d, new Date(), campaignEnd || null);
  const ready = isReady(card);
  const need =
    card.type === 'task'
      ? {
          text: card.done ? 'Completed' : card.untitled ? 'Needs a name' : 'Not done yet',
          ok: card.done,
        }
      : needWords(card.fields);
  const st = statusWords(card.status, approver, canApprove);
  const allOk = card.fields.every((f) => f.ok);
  const untracked = isNotTracked(card);
  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };
  return (
    <div
      className="cb-card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target === e.currentTarget) onOpen();
      }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <div className="cb-card-top">
        <span className="cb-chip">{card.kind}</span>
        <span className="cb-card-right">
          {!ready && d ? (
            <span className="cb-due" style={{ color: due.ink }}>
              {due.text}
            </span>
          ) : null}
          {ready && cantGoOut ? (
            <span className="cb-cant-post">Can’t post until you reconnect</span>
          ) : ready ? (
            <span className="cb-ready">✓ {card.done ? 'Done' : 'Ready'}</span>
          ) : null}
        </span>
      </div>
      <div className="cb-card-text" data-untitled={card.untitled ? 'true' : undefined}>
        {card.text}
      </div>
      {untracked ? (
        <div className="cb-untracked">{notTrackedLine(card)}</div>
      ) : (
        <div className="cb-bars">
          {card.type === 'task' ? (
            // A task is done or not: one bar, empty until it's done (round 20; its
            // Title + Done pair read as half done).
            <span title={card.done ? 'Done' : 'To do'} data-state={card.done ? 'all' : undefined} />
          ) : (
            card.fields.map((f) => (
              <span
                key={f.label}
                title={`${f.label}${f.ok ? ' ✓' : ' · missing'}`}
                data-state={f.ok ? (allOk ? 'all' : 'ok') : undefined}
              />
            ))
          )}
        </div>
      )}
      <div className="cb-card-foot">
        <span className="cb-need" data-ok={need.ok ? 'true' : undefined}>
          {untracked ? '' : need.text}
        </span>
        <span className="cb-card-right">
          {card.link ? (
            <a
              className="cb-note-chip cb-card-link"
              href={card.link.url}
              target="_blank"
              rel="noopener noreferrer"
              title={card.link.url}
              onClick={(e) => e.stopPropagation()}
            >
              ↗ {linkName(card.link)}
            </a>
          ) : null}
          {card.note ? (
            <button
              type="button"
              className="cb-note-chip"
              title="Notes"
              onMouseEnter={onPeek}
              onFocus={onPeek}
              onClick={stop(onEdit)}
            >
              ✎ Note
            </button>
          ) : hovered && !editing ? (
            <button type="button" className="cb-note-add" onClick={stop(onEdit)}>
              + Note
            </button>
          ) : null}
          <span className="cb-status" style={{ color: st.ink }}>
            <span className="cb-dot" style={{ background: st.dot }} />
            {st.label}
          </span>
        </span>
      </div>
      {editing || (card.note && peeking) ? (
        <div
          className="cb-pop"
          role="dialog"
          aria-label="Card notes"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="cb-pop-label">NOTES</div>
          {editing ? (
            <>
              <textarea
                className="cb-input"
                rows={5}
                autoFocus
                value={editing.text}
                placeholder="Notes"
                onChange={(e) => onEditChange({ text: e.target.value })}
              />
              <div className="cb-pop-label">LINK</div>
              <input
                className="cb-input"
                aria-label="Link to its file or doc"
                placeholder="https://example.com/doc"
                value={editing.linkUrl}
                onChange={(e) => onEditChange({ linkUrl: e.target.value })}
              />
              {editing.linkUrl.trim() ? (
                <input
                  className="cb-input"
                  aria-label="Link name"
                  placeholder="Link name"
                  value={editing.linkLabel}
                  onChange={(e) => onEditChange({ linkLabel: e.target.value })}
                />
              ) : null}
              <label className="cb-due-field">
                Due
                <input
                  type="date"
                  className="cb-input"
                  value={editing.due}
                  onChange={(e) => onEditChange({ due: e.target.value })}
                />
              </label>
              <div className="cb-actions">
                <button type="button" className="ink-btn" onClick={onEditCancel}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="ink-btn ink-btn-primary"
                  disabled={saving}
                  onClick={onEditSave}
                >
                  Save
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="cb-pop-text">{card.note}</div>
              <div className="cb-pop-foot">
                <span>{dueFull(d)}</span>
                <button type="button" className="ink-link-btn" onClick={stop(onEdit)}>
                  Edit
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ── The drawer ───────────────────────────────────────────────────────────────

const ASSET_URL_QUERY = gql`
  query CampaignBoardAsset($accountId: ID!, $id: ID!) {
    mediaAsset(accountId: $accountId, id: $id) {
      id
      sha256
      originalFilename
      renditions {
        kind
        url
        isCurrent
      }
    }
  }
`;

/**
 * A card's image (Prototype: "Choose from Media or upload"): from the site's
 * Media, an upload (which lands in Media too), or an address. Everyone on the
 * campaign's team, the Campaign role included.
 */
function ImageRow({
  accountId,
  label,
  url,
  onChange,
}: {
  accountId: string;
  label: string;
  url: string;
  onChange: (url: string) => void;
}) {
  const apollo = useApolloClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [typing, setTyping] = useState(false);
  const [draft, setDraft] = useState('');
  const [browsing, setBrowsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const upload = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const { assetId } = await uploadAsset(accountId, file, { filename: file.name });
      const { data } = await apollo.query<{ mediaAsset: LibraryAsset | null }>({
        query: ASSET_URL_QUERY,
        variables: { accountId, id: assetId },
        fetchPolicy: 'network-only',
      });
      const u = data?.mediaAsset ? currentUrl(data.mediaAsset) : null;
      if (u) onChange(u);
      else showInkToast('Uploaded to Media, but it isn’t ready yet. Choose it from Media.');
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="cb-field">
      <span className="cb-field-label">{label}</span>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          void upload(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {url ? (
        <div className="cb-img">
          {/\.mp4(\?|$)/i.test(url) ? (
            <video src={url} muted playsInline preload="metadata" />
          ) : (
            <img src={url} alt="" />
          )}
          <span className="cb-img-from">{url.replace(/^https?:\/\//, '')}</span>
          <button type="button" className="ink-quiet-link" onClick={() => setBrowsing(true)}>
            Change
          </button>
          <button type="button" className="ink-quiet-link" onClick={() => onChange('')}>
            Remove
          </button>
        </div>
      ) : typing ? (
        <div className="cb-img-url">
          <input
            className="cb-input"
            type="url"
            autoFocus
            value={draft}
            placeholder="https://example.com/photo.jpg"
            aria-label="Image address"
            onChange={(e) => setDraft(e.target.value)}
          />
          <button
            type="button"
            className="ink-btn ink-btn-primary"
            disabled={!/^https?:\/\/\S+$/i.test(draft.trim())}
            onClick={() => {
              onChange(draft.trim());
              setTyping(false);
            }}
          >
            Use image
          </button>
          <button type="button" className="ink-quiet-link" onClick={() => setTyping(false)}>
            Cancel
          </button>
        </div>
      ) : (
        <div className="cb-img-empty">
          <button type="button" className="ink-link-btn" onClick={() => setBrowsing(true)}>
            Choose from Media
          </button>
          <span className="ink-muted">or</span>
          <button
            type="button"
            className="ink-link-btn"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? 'Uploading…' : 'upload'}
          </button>
          <span className="ink-muted">·</span>
          <button type="button" className="ink-link-btn" onClick={() => setTyping(true)}>
            Use an image address
          </button>
        </div>
      )}
      {browsing ? (
        <MediaUrlPicker
          accountId={accountId}
          note={`The ${label.toLowerCase()} for this card. Images you upload here are added to Media too.`}
          onPick={(u) => {
            onChange(u);
            setBrowsing(false);
          }}
          onClose={() => setBrowsing(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * A landing page the campaign didn't make (round 34, backlog fdfabdf8): say
 * that its sign-ups won't count, and offer a campaign page instead.
 */
function NotTrackedDialog({
  card,
  canMake,
  onKeep,
  onMake,
}: {
  card: BoardCard;
  canMake: boolean;
  onKeep: () => void;
  onMake: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const l = card.landing;
  const other = l?.kind === 'url';
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onKeep();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onKeep]);
  return (
    <div
      className="ink-modal-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onKeep();
      }}
    >
      <div className="ink-modal cb-untracked-dlg" role="dialog" aria-label="Landing page">
        <div className="cb-untracked-head">
          <span className="ink-modal-title">Landing page</span>
          <span className="cb-untracked-pill">Not tracked</span>
        </div>
        <div className="cb-untracked-box">
          <span>{other ? 'Another site' : l?.title || 'Page'}</span>
          <span className="cb-untracked-path">{other ? l?.title : l?.path}</span>
        </div>
        <p className="ink-modal-copy">
          {other
            ? 'Posts link to another site, so sign-ups and visits there won’t count toward this campaign’s results.'
            : `Posts link here, but it isn’t a campaign page, so sign-ups and visits here won’t count toward this campaign’s results.`}
        </p>
        <div className="ink-modal-actions">
          <button type="button" className="ink-btn" disabled={busy} onClick={onKeep}>
            Keep this page
          </button>
          {canMake ? (
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void onMake().finally(() => setBusy(false));
              }}
            >
              Make a campaign page
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function CardDrawer({
  accountId,
  campaign,
  card,
  board,
  approver,
  onClose,
  onSaved,
}: {
  accountId: string;
  campaign: CampaignLite;
  card: BoardCard;
  board: Board;
  approver: string | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const full = board.viewer.canApprove;
  const a = card.article;
  const l = card.landing;
  const [idea, setIdea] = useState(a?.idea || '');
  const [title, setTitle] = useState(
    card.type === 'task' ? (card.untitled ? '' : card.text) : a?.title || l?.headline || ''
  );
  const [summary, setSummary] = useState(a?.summary || '');
  const [body, setBody] = useState(a?.body || '');
  const [copy, setCopy] = useState(l?.copy || '');
  const [hero, setHero] = useState(a?.heroUrl || l?.heroUrl || '');
  const d0 = dueDay(card);
  const [taskDue, setTaskDue] = useState(d0 ? toYmd(d0) : '');
  const [taskDone, setTaskDone] = useState(card.done);
  const [busy, setBusy] = useState(false);
  const [saveArticle] = useMutation(SAVE_CAMPAIGN_ARTICLE);
  const [saveLanding] = useMutation(SAVE_CAMPAIGN_LANDING);
  const [saveTask] = useMutation(SAVE_CAMPAIGN_TASK);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const due = dueWords(d0, new Date(), calendarDate(campaign.deadline));
  const need = needWords(card.fields);
  const editable =
    card.type === 'article' ||
    card.type === 'task' ||
    (card.type === 'landing' && l?.kind === 'page');

  const save = async () => {
    setBusy(true);
    try {
      if (card.type === 'article') {
        const input: Record<string, unknown> = { title: title.trim() };
        if (idea !== (a?.idea || '')) input.idea = idea;
        if (summary !== (a?.summary || '')) input.summary = summary;
        if (body !== (a?.body || '')) input.body = body;
        if (hero !== (a?.heroUrl || '')) input.heroUrl = hero || null;
        await saveArticle({ variables: { id: card.id, input } });
        showInkToast(
          title.trim()
            ? 'Saved to the campaign. Nothing goes live until it’s approved.'
            : 'Saved. Give it a headline to start the article.'
        );
      } else if (card.type === 'landing') {
        const input: Record<string, unknown> = {};
        if (title !== (l?.headline || '')) input.headline = title;
        if (copy !== (l?.copy || '')) input.copy = copy;
        if (hero !== (l?.heroUrl || '')) input.heroUrl = hero || null;
        if (Object.keys(input).length) {
          await saveLanding({ variables: { campaignId: campaign.id, input } });
        }
        showInkToast('Saved to the campaign. Nothing goes live until it’s approved.');
      } else if (card.type === 'task') {
        await saveTask({
          variables: {
            id: card.id,
            title: title.trim(),
            done: taskDone,
            ...(taskDue ? { dueDate: taskDue } : {}),
          },
        });
        showInkToast('Task saved');
      }
      await onSaved();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(false);
    }
  };

  const fullLink =
    !full || card.type === 'task' || card.type === 'plan'
      ? null
      : card.type === 'article' && card.topicId
        ? { label: 'Open in Website → Articles', to: `/content/${card.topicId}` }
        : card.type === 'landing' && card.pageId
          ? { label: 'Open in Website → Pages', to: `/content?view=pages&page=${card.pageId}` }
          : card.type === 'form' && card.formId
            ? { label: 'Open in Website → Forms', to: `/content?view=forms&form=${card.formId}` }
            : null;

  const head =
    card.type === 'form'
      ? card.form?.name || 'Sign-up form'
      : card.type === 'task'
        ? title || 'New task'
        : card.type === 'article'
          ? title || (idea ? `Untitled · ${idea}` : 'Untitled article')
          : card.type === 'landing'
            ? title || l?.title || campaign.name
            : card.text;

  return (
    <>
      <div className="cb-drawer-back" onClick={() => !busy && onClose()} />
      <div className="cb-drawer" role="dialog" aria-label={`${card.kind}: ${head}`}>
        <div className="cb-drawer-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="cb-card-top" style={{ justifyContent: 'flex-start' }}>
              <span className="cb-chip">{card.kind}</span>
              {d0 && !isReady(card) ? (
                <span className="cb-due" style={{ color: due.ink }}>
                  {due.text}
                </span>
              ) : null}
            </div>
            <div className="cb-drawer-title">{head}</div>
            <div className="cb-drawer-sub">
              {campaign.name} ·{' '}
              {card.type === 'task' ? (card.done ? 'Completed' : 'Not done yet') : need.text}
            </div>
          </div>
          <button type="button" className="cb-x" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="cb-drawer-body">
          {card.note ? (
            <div className="cb-drawer-note">
              <span>NOTES</span>
              <div>{card.note}</div>
            </div>
          ) : null}

          {card.type === 'article' ? (
            <>
              {!card.topicId ? (
                <label className="cb-field">
                  <span className="cb-field-label">
                    Working idea{' '}
                    <span className="ink-muted">· the card’s name until it has a headline</span>
                  </span>
                  <input
                    className="cb-input"
                    value={idea}
                    placeholder="Idea"
                    onChange={(e) => setIdea(e.target.value)}
                  />
                </label>
              ) : null}
              <label className="cb-field">
                <span className="cb-field-label">Headline</span>
                <input
                  className="cb-input cb-input-lg"
                  value={title}
                  placeholder="Headline"
                  onChange={(e) => setTitle(e.target.value)}
                />
              </label>
              <label className="cb-field">
                <span className="cb-field-label">
                  Summary <span className="ink-muted">· used in cards, feeds and posts</span>
                </span>
                <textarea
                  className="cb-input"
                  rows={2}
                  value={summary}
                  placeholder="Summary"
                  onChange={(e) => setSummary(e.target.value)}
                />
              </label>
              <label className="cb-field">
                <span className="cb-field-label">Body</span>
                <textarea
                  className="cb-input"
                  rows={8}
                  value={body}
                  placeholder="Body"
                  onChange={(e) => setBody(e.target.value)}
                  style={{ lineHeight: 1.6 }}
                />
              </label>
              <ImageRow accountId={accountId} label="Lead image" url={hero} onChange={setHero} />
            </>
          ) : null}

          {card.type === 'landing' ? (
            l?.kind === 'page' ? (
              <>
                <label className="cb-field">
                  <span className="cb-field-label">Headline</span>
                  <input
                    className="cb-input cb-input-lg"
                    value={title}
                    placeholder="Headline"
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </label>
                <label className="cb-field">
                  <span className="cb-field-label">Page copy</span>
                  <textarea
                    className="cb-input"
                    rows={4}
                    value={copy}
                    placeholder="Page copy"
                    onChange={(e) => setCopy(e.target.value)}
                    style={{ lineHeight: 1.55 }}
                  />
                </label>
                <div className="cb-field">
                  <span className="cb-field-label">Form</span>
                  <div className="cb-box">
                    {l.formName ? (
                      <>
                        <span className="cb-chip">Sign-up form</span>
                        {l.formName}
                      </>
                    ) : (
                      <span className="ink-muted">No form on this page yet.</span>
                    )}
                  </div>
                </div>
                <ImageRow accountId={accountId} label="Hero image" url={hero} onChange={setHero} />
                <p className="cb-help">
                  {l.path} · {l.live ? 'Live. Changes wait for approval.' : 'Not live yet.'}
                </p>
              </>
            ) : (
              <p className="cb-help">
                {l?.kind === 'agent'
                  ? 'Your agent drafts the landing page, with a sign-up form, from the campaign’s brief. It waits for approval like any page.'
                  : `Posts link to ${l?.url || 'another address'}. Sign-ups there won’t show in the campaign’s results.`}
              </p>
            )
          ) : null}

          {card.type === 'form' && card.form ? (
            <>
              <div className="cb-field">
                <span className="cb-field-label">Fields</span>
                <div className="cb-tags">
                  {card.form.fields.map((f) => (
                    <span key={f.label}>
                      {f.label}
                      {f.required ? ' · required' : ''}
                    </span>
                  ))}
                </div>
              </div>
              <div className="cb-field">
                <span className="cb-field-label">
                  Confirmation message{' '}
                  <span className="ink-muted">· shown after someone signs up</span>
                </span>
                <div className="cb-box">
                  {card.form.successMessage || <span className="ink-muted">None yet.</span>}
                </div>
              </div>
            </>
          ) : null}

          {card.type === 'task' ? (
            <>
              <label className="cb-field">
                <span className="cb-field-label">Task</span>
                <input
                  className="cb-input cb-input-lg"
                  value={title}
                  autoFocus={card.untitled}
                  placeholder="Task"
                  onChange={(e) => setTitle(e.target.value)}
                />
              </label>
              <label className="cb-due-field">
                Due
                <input
                  type="date"
                  className="cb-input"
                  value={taskDue}
                  onChange={(e) => setTaskDue(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="cb-check"
                data-on={taskDone ? 'true' : undefined}
                onClick={() => setTaskDone((v) => !v)}
              >
                <span aria-hidden="true">{taskDone ? '✓' : ''}</span>
                Done
              </button>
              <p className="cb-help">
                Add details with the note on the card. Anyone on the campaign team can see and edit
                it.
              </p>
            </>
          ) : null}

          {card.type === 'plan' && card.plan ? (
            <>
              <div className="cb-box cb-box-text">{card.plan.content || card.plan.title}</div>
              <p className="cb-help">
                Part of your agent’s plan.{' '}
                {full
                  ? 'Approving the campaign puts it on the calendar.'
                  : `${approver || 'The owner'} approves the campaign, and then it goes on the calendar.`}
              </p>
              {full ? (
                <button
                  type="button"
                  className="ink-link-btn"
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => navigate('/reach/campaigns/manage')}
                >
                  Edit planned posts →
                </button>
              ) : null}
            </>
          ) : null}
        </div>
        <div className="cb-drawer-foot">
          {fullLink ? (
            <button type="button" className="ink-link-btn" onClick={() => navigate(fullLink.to)}>
              {fullLink.label}
            </button>
          ) : null}
          <span style={{ flex: 1 }} />
          <button type="button" className="ink-btn" disabled={busy} onClick={onClose}>
            {editable ? 'Cancel' : 'Close'}
          </button>
          {editable ? (
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              disabled={busy}
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
}
